import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { Tag } from '~/components/Tag';

import { AppliedFilterListItem } from './AppliedFilterListItem';

export interface AppliedLabelFilter {
  labelId: number;
  displayName: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface AppliedFilterListProps {
  isCrossover: boolean;
  isOnRoster?: boolean;
  labelFilters: AppliedLabelFilter[];
  // @TODO WMG1-572 - Put back as a required prop once back end data is fixed.
  // onRemoveCrossover: () => void;
  onRemoveOnRoster: () => void;
  onRemoveLabel: (labelId: number) => void;
  onClear: () => void;
  onMoreClick?: () => void;
}

function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  delay: number,
) {
  let timeout: ReturnType<typeof setTimeout>;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}

function AppliedFilterList({
  isCrossover,
  isOnRoster,
  labelFilters,
  // @TODO WMG1-572 - Put back as a required prop once back end data is fixed.
  // onRemoveCrossover,
  onRemoveOnRoster,
  onRemoveLabel,
  onClear,
  onMoreClick,
}: AppliedFilterListProps) {
  const { t } = useTranslation('roster');
  const [maxVisibleFilters, setMaxVisibleFilters] = useState(4);
  const visibleLabelFilterCount = maxVisibleFilters - (isCrossover ? 1 : 0);

  const calculateMaxVisibleFilters = useCallback(() => {
    if (window.innerWidth < 768) {
      return 1;
    }

    if (window.innerWidth < 1024) {
      return 2;
    }

    if (window.innerWidth < 1368) {
      return 3;
    }

    if (window.innerWidth < 1400) {
      return 4;
    }

    return 5; // Default for large screens
  }, []);

  useEffect(() => {
    const handleResize = debounce(() => {
      setMaxVisibleFilters(calculateMaxVisibleFilters());
    }, 200); // Debounce by 200ms

    // Set initial value
    setMaxVisibleFilters(calculateMaxVisibleFilters());

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateMaxVisibleFilters]);

  return (
    <div
      className="flex items-center gap-md py-xs"
      aria-label="Applied filters"
    >
      <div className="text-body-sm">{t('Filter(s) applied:')}</div>
      {/* @TODO WMG1-572 - Put back in once back end data is fixed. */}
      {/* {isCrossover ? (
        <AppliedFilterListItem
          label={t('crossover.label')}
          onRemove={onRemoveCrossover}
        />
      ) : null} */}
      {isOnRoster !== undefined ? (
        <AppliedFilterListItem
          label={t(
            isOnRoster ? 'filters.onRoster.label' : 'filters.offRoster.label',
          )}
          onRemove={onRemoveOnRoster}
        />
      ) : null}
      {labelFilters
        .slice(0, visibleLabelFilterCount)
        .map(({ labelId, displayName, updatedAt, updatedBy }) => {
          const tooltipContent = updatedAt ? (
            <div className="text-body-xs font-semibold leading-tight">
              <p>
                {t('labels.lastUpdated')}:{' '}
                {new Date(updatedAt).toLocaleDateString()}
              </p>
              <p>
                {t('labels.by')}: {updatedBy}
              </p>
            </div>
          ) : null;

          return (
            <AppliedFilterListItem
              key={labelId}
              label={displayName}
              tooltipContent={tooltipContent}
              onRemove={() => onRemoveLabel(labelId)}
            />
          );
        })}
      {labelFilters.length > visibleLabelFilterCount ? (
        <Tag
          onClick={onMoreClick}
          variant="secondary"
          key="more"
          className="gap-xs pr-sm"
        >
          {t('labels.moreLink', {
            count: labelFilters.length - visibleLabelFilterCount,
          })}
        </Tag>
      ) : null}
      <Button
        variant="text"
        onClick={onClear}
        className="-ml-sm px-sm py-xs text-body-sm font-semibold"
      >
        {t('filters.clearFilters')}
      </Button>
    </div>
  );
}

AppliedFilterList.displayName = 'AppliedFilterList';

export { AppliedFilterList };
